/**
 * Template de prise de besoin : After-Action Report (AAR) v1
 * Aligné sur NIST CSF 2.0, ITIL 4 et ISO 27001
 * Rapport détaillé d'action après incident
 */

import type { IntakeTemplate } from '../templateTypes';

export const templateAfterActionReportV1: IntakeTemplate = {
  id: 'after-action-report-v1',
  name: 'Prise de besoin After-Action Report (AAR) v1',
  description:
    'Questionnaire complet pour élaborer un After-Action Report conforme NIST CSF 2.0, ITIL 4 et ISO 27001. Documente la chronologie, les actions prises, les résultats et les recommandations.',
  documentTypeCode: 'AFTER_ACTION_REPORT',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Identification',
      description: 'Informations sur l\'incident et l\'entreprise',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour le rapport.',
        },
        {
          id: 'nom_incident',
          label: 'Nom de l\'incident',
          type: 'text',
          placeholder: 'Ex: Incident de sécurité - Ransomware',
          required: true,
          helpText: 'Nom descriptif de l\'incident pour identification.',
        },
        {
          id: 'date_debut',
          label: 'Date de début de l\'incident',
          type: 'date',
          required: true,
          helpText: 'Date de début de l\'incident.',
        },
        {
          id: 'date_fin',
          label: 'Date de fin de l\'incident',
          type: 'date',
          helpText: 'Date de résolution de l\'incident.',
        },
        {
          id: 'type_incident',
          label: 'Type d\'incident',
          type: 'select',
          options: [
            'Incident de sécurité',
            'Incident de disponibilité',
            'Incident d\'intégrité',
            'Incident de confidentialité',
            'Incident majeur',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0: Classification du type d\'incident.',
        },
        {
          id: 'severite_incident',
          label: 'Sévérité de l\'incident',
          type: 'select',
          options: ['CAT 1', 'CAT 2', 'CAT 3', 'CAT 4', 'CAT 5'],
          required: true,
          helpText: 'NIST CSF 2.0: Sévérité selon classification NIST.',
        },
        {
          id: 'ton_document',
          label: 'Quel ton souhaitez-vous pour ce rapport ?',
          type: 'select',
          options: [
            'Formel et analytique (ton professionnel, analyse approfondie)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité de compréhension du rapport.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Chronologie Détaillée',
      description: 'Chronologie complète de l\'incident',
      icon: 'Clock',
      questions: [
        {
          id: 'chronologie_complete',
          label: 'Chronologie complète de l\'incident (timeline détaillée)',
          type: 'textarea',
          placeholder: 'Décrivez tous les événements dans l\'ordre chronologique avec heures précises: Détection → Analyse → Confinement → Éradication → Restauration...',
          required: true,
          helpText: 'NIST CSF 2.0: Chronologie complète pour documentation exhaustive.',
        },
        {
          id: 'evenements_cles',
          label: 'Quels sont les événements clés de l\'incident ?',
          type: 'textarea',
          placeholder: 'Listez les événements marquants: détection, escalade, décisions importantes...',
          helpText: 'NIST CSF 2.0: Événements clés pour compréhension de l\'incident.',
        },
        {
          id: 'points_decision',
          label: 'Quels sont les points de décision importants ?',
          type: 'textarea',
          placeholder: 'Décrivez les décisions critiques prises: activation équipe, confinement, communication...',
          helpText: 'NIST CSF 2.0: Points de décision pour analyse de la réponse.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Actions & Résultats',
      description: 'Actions prises et résultats obtenus',
      icon: 'FileCheck',
      questions: [
        {
          id: 'actions_prises',
          label: 'Quelles actions ont été prises pour répondre à l\'incident ?',
          type: 'textarea',
          placeholder: 'Décrivez toutes les actions: détection, analyse, confinement, éradication, restauration, communication...',
          required: true,
          helpText: 'NIST CSF 2.0 (RS.MI): Actions pour documentation complète.',
        },
        {
          id: 'resultats_obtenus',
          label: 'Quels résultats ont été obtenus ?',
          type: 'textarea',
          placeholder: 'Décrivez les résultats: succès, échecs partiels, impacts...',
          required: true,
          helpText: 'NIST CSF 2.0: Résultats pour évaluation de l\'efficacité.',
        },
        {
          id: 'objectifs_atteints',
          label: 'Les objectifs de réponse ont-ils été atteints ?',
          type: 'select',
          options: ['Oui, tous les objectifs', 'Partiellement', 'Non', 'À évaluer'],
          helpText: 'NIST CSF 2.0: Objectifs pour évaluation de la performance.',
        },
        {
          id: 'ecarts_objectifs',
          label: 'Quels écarts avec les objectifs ont été constatés ?',
          type: 'textarea',
          placeholder: 'Décrivez les écarts: délais, qualité, ressources...',
          helpText: 'NIST CSF 2.0: Écarts pour identification des améliorations.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Analyse & Impact',
      description: 'Analyse de cause racine et impact',
      icon: 'Target',
      questions: [
        {
          id: 'cause_racine',
          label: 'Quelle est la cause racine identifiée ?',
          type: 'textarea',
          placeholder: 'Décrivez la cause racine de l\'incident...',
          required: true,
          helpText: 'NIST CSF 2.0: Cause racine pour prévention des récurrences.',
        },
        {
          id: 'impact_business',
          label: 'Quel a été l\'impact business de l\'incident ?',
          type: 'textarea',
          placeholder: 'Décrivez l\'impact: interruption de service, perte de données, impact financier, réputation, conformité...',
          required: true,
          helpText: 'NIST CSF 2.0: Impact business pour évaluation des risques.',
        },
        {
          id: 'impact_technique',
          label: 'Quel a été l\'impact technique de l\'incident ?',
          type: 'textarea',
          placeholder: 'Décrivez l\'impact technique: systèmes affectés, données compromises, infrastructure...',
          helpText: 'NIST CSF 2.0: Impact technique pour évaluation complète.',
        },
        {
          id: 'metriques_performance',
          label: 'Quelles métriques de performance peuvent être extraites ?',
          type: 'textarea',
          placeholder: 'Décrivez les métriques: MTTD, MTTR, taux de résolution, satisfaction...',
          helpText: 'NIST CSF 2.0: Métriques pour mesure de la performance.',
        },
      ],
    },
    {
      id: 'phase5',
      title: 'Recommandations & Suivi',
      description: 'Recommandations et plan de suivi',
      icon: 'TrendingUp',
      questions: [
        {
          id: 'recommandations',
          label: 'Quelles sont les recommandations principales ?',
          type: 'textarea',
          placeholder: 'Décrivez les recommandations: processus, outils, formation, documentation, prévention...',
          required: true,
          helpText: 'NIST CSF 2.0: Recommandations pour amélioration continue.',
        },
        {
          id: 'actions_prioritaires',
          label: 'Quelles sont les actions prioritaires à mettre en place ?',
          type: 'textarea',
          placeholder: 'Listez les actions prioritaires avec délais...',
          required: true,
          helpText: 'NIST CSF 2.0: Actions prioritaires pour plan d\'action.',
        },
        {
          id: 'responsables_suivi',
          label: 'Qui sont les responsables du suivi des recommandations ?',
          type: 'textarea',
          placeholder: 'Décrivez les responsables: rôles, responsabilités...',
          helpText: 'NIST CSF 2.0: Responsables pour garantie de suivi.',
        },
        {
          id: 'delais_implementation',
          label: 'Quels sont les délais d\'implémentation prévus ?',
          type: 'textarea',
          placeholder: 'Décrivez les délais: actions immédiates, court terme, long terme...',
          helpText: 'NIST CSF 2.0: Délais pour planification de l\'implémentation.',
        },
      ],
    },
  ],
};





